import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AuthoringAdminService from "../../../apis/AuthoringAdminService";
import {
  extractColumnsFromRows,
  formatKeyToLabel,
  formatValueForDetails,
  formatValueForTable,
  normalizeApiListResponse,
  sanitizeRowsForDisplay,
} from "../shared/tableUtils";

const DEFAULT_ROWS_PER_PAGE = 10;

const YEAR_START = 2025;
const YEAR_END = 2030;

const createEmptyFilters = () => ({
  year: "",
  email: "",
});

const ContactsManager = () => {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [filters, setFilters] = useState(createEmptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(createEmptyFilters);
  const [refreshIndex, setRefreshIndex] = useState(0);

  const columns = useMemo(() => extractColumnsFromRows(rows), [rows]);
  const availableYears = useMemo(() => {
    return Array.from({ length: YEAR_END - YEAR_START + 1 }, (_, index) => {
      return YEAR_START + index;
    });
  }, []);

  const { year: appliedYear, email: appliedEmail } = appliedFilters;
  const hasAnyFilterApplied = Boolean(appliedYear) || Boolean(appliedEmail);
  const hasFilterInput =
    Boolean(filters.year?.trim()) || Boolean(filters.email?.trim());
  const isResetDisabled = !hasAnyFilterApplied && !hasFilterInput;

  const loadContacts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await AuthoringAdminService.fetchContactPosts({
        page,
        pageSize: rowsPerPage,
        year: appliedYear,
        email: appliedEmail,
      });

      const { items, total, page: responsePage, pageSize: responsePageSize } =
        normalizeApiListResponse(response);

      const itemsArray = Array.isArray(items) ? items : [];
      const sanitizedRows = sanitizeRowsForDisplay(itemsArray);

      setRows(sanitizedRows);
      setTotalCount(typeof total === "number" ? total : 0);

      if (typeof responsePage === "number" && Number.isFinite(responsePage)) {
        const normalizedPage = Math.max(responsePage - 1, 0);
        if (normalizedPage !== page) {
          setPage(normalizedPage);
        }
      }

      if (
        typeof responsePageSize === "number" &&
        Number.isFinite(responsePageSize) &&
        responsePageSize > 0 &&
        responsePageSize !== rowsPerPage
      ) {
        setRowsPerPage(responsePageSize);
      }
    } catch (requestError) {
      console.error("Failed to load contact posts", requestError);
      const message =
        requestError?.response?.data?.message ||
        requestError?.message ||
        "Unable to load contact posts right now.";
      setError(message);
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [appliedEmail, appliedYear, page, refreshIndex, rowsPerPage]);

  useEffect(() => {
    void loadContacts();
  }, [loadContacts]);

  const handleChangePage = (_, newPage) => {
    if (newPage === page) {
      return;
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = Number(event.target.value);

    if (!Number.isFinite(newSize) || newSize <= 0) {
      return;
    }

    setRowsPerPage(newSize);
    setPage(0);
  };

  const handleRowClick = (row) => {
    setSelectedRow(row);
  };

  const handleCloseDialog = () => {
    setSelectedRow(null);
  };

  const handleFilterInputChange = (field) => (event) => {
    const { value } = event.target;
    setFilters((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleRefresh = () => {
    setRefreshIndex((previous) => previous + 1);
  };

  const handleApplyFilters = () => {
    const sanitizedFilters = {
      year: filters.year?.trim() || "",
      email: filters.email?.trim() || "",
    };

    const isSameYear = sanitizedFilters.year === appliedYear;
    const isSameEmail = sanitizedFilters.email === appliedEmail;

    setFilters(sanitizedFilters);
    setAppliedFilters({ ...sanitizedFilters });

    if (isSameYear && isSameEmail) {
      handleRefresh();
      return;
    }

    setPage(0);
  };

  const handleResetFilters = () => {
    const isSameYear = appliedYear === "";
    const isSameEmail = appliedEmail === "";

    setFilters(createEmptyFilters());
    setAppliedFilters(createEmptyFilters());

    if (isSameYear && isSameEmail) {
      handleRefresh();
      return;
    }

    setPage(0);
  };

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            flexGrow={1}
            sx={{ width: "100%" }}
          >
            <TextField
              select
              label="Year"
              value={filters.year}
              onChange={handleFilterInputChange("year")}
              sx={{ minWidth: { sm: 140 }, flexBasis: { sm: "30%" } }}
            >
              <MenuItem value="">All years</MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={year} value={String(year)}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Email"
              type="email"
              value={filters.email}
              onChange={handleFilterInputChange("email")}
              sx={{ flexGrow: 0, flexBasis: { sm: "40%" }, minWidth: { sm: 220 } }}
            />
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent="flex-end"
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Button
              variant="outlined"
              onClick={handleResetFilters}
              disabled={isResetDisabled}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              startIcon={<RefreshIcon fontSize="small" />}
              disabled={loading}
              sx={{ minWidth: { xs: "100%", sm: 160 }, whiteSpace: "nowrap" }}
            >
              Apply filters
            </Button>
            <Button
              variant="outlined"
              onClick={handleRefresh}
              startIcon={<RefreshIcon fontSize="small" />}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ position: "relative" }}>
        {loading && <LinearProgress />}

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && rows.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>
            No contact posts available yet.
          </Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column}>{formatKeyToLabel(column)}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow
                    key={index}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleRowClick(row)}
                  >
                    {columns.map((column) => (
                      <TableCell key={column}>
                        {formatValueForTable(row?.[column])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {totalCount > 0 && (
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}
      </Paper>

      <Dialog open={Boolean(selectedRow)} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Contact details</DialogTitle>
        <DialogContent dividers>
          {selectedRow ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {Object.entries(selectedRow).map(([key, value]) => (
                <Box key={key}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {formatKeyToLabel(key)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component={typeof value === "object" ? "pre" : "p"}
                    sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", m: 0 }}
                  >
                    {formatValueForDetails(value)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2">No contact selected.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ContactsManager;
